<template>
  <div class="case-body py-3 px-4">
    <div class="form-group">
      <label class="mb-1 text-light">Year</label>
      <select class="custom-select custom-select-sm bg-dark-input text-white" v-model.number="models.year">
        <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
      </select>
    </div>
    <div class="form-row">
      <div class="form-group col">
        <label class="mb-1 text-light">Contract Starts</label>
        <select class="custom-select custom-select-sm bg-dark-input text-white" v-model.number="models.startMonth">
          <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">{{ m }}</option>
        </select>
      </div>
      <div class="form-group col">
        <label class="mb-1 text-light">Months</label>
        <select class="custom-select custom-select-sm bg-dark-input text-white" v-model.number="models.monthCount">
          <option v-for="n in maxMonthCount" :key="n" :value="n">{{ n }}</option>
        </select>
      </div>
    </div>
    <div class="form-group">
      <label class="mb-1 text-light">As At</label>
      <select class="custom-select custom-select-sm bg-dark-input text-white" v-model.number="models.asAtMonth">
        <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">{{ m }}</option>
      </select>
    </div>
    <div class="form-group">
      <!-- Select/deselect all is a text action on this label row, not a
           checkbox at the top of the list below (INFS-1454). As a checkbox it
           sat in the same column as the districts and read as one of them —
           ticking "everything" looked like ticking a district called
           "Select all". -->
      <div class="d-flex justify-content-between align-items-baseline mb-1">
        <label class="mb-0 text-light">Districts</label>
        <a href="#" class="kjp-select-all" :class="{ disabled: !districts.length }" @click.prevent="toggleAll">
          {{ allSelected ? "Deselect all" : "Select all" }}
        </a>
      </div>
      <div class="kjp-district-list">
        <div class="custom-control custom-checkbox" v-for="d in districts" :key="d._id">
          <input type="checkbox" class="custom-control-input" :id="`kjp-d-${d._id}`" :value="d._id"
            v-model="models.districtIds" />
          <label class="custom-control-label small text-light" :for="`kjp-d-${d._id}`">{{ d.name }}</label>
        </div>
      </div>
    </div>
    <div class="text-right">
      <button class="btn btn-primary btn-sm px-4" :disabled="!models.districtIds.length" @click="apply">
        Apply
      </button>
    </div>
  </div>
</template>

<script>
import { KJP_SCOPE_CODES } from "@/components/kjp/scopes";

export default {
  name: "kjp-controls",
  props: ["districts"],
  data() {
    const now = new Date();

    return {
      monthNames: ["January", "February", "March", "April", "May", "June",
                   "July", "August", "September", "October", "November", "December"],
      models: {
        year: now.getFullYear(),
        startMonth: 1,
        monthCount: 12,
        asAtMonth: now.getMonth() + 1,
        districtIds: [],
      },
    };
  },
  computed: {
    years() {
      const y = new Date().getFullYear();

      return [y + 1, y, y - 1, y - 2];
    },
    allSelected() {
      return this.districts.length > 0 && this.models.districtIds.length === this.districts.length;
    },
    // A contract can't run past December, so the number of months on offer
    // shrinks as the start month moves later (start June + 12 months would
    // otherwise mean a contract running to month 17).
    maxMonthCount() {
      return 13 - this.models.startMonth;
    },
  },
  watch: {
    districts: {
      handler() {
        this.models.districtIds = this.districts.map((d) => d._id);
      },
      immediate: true,
    },
    // Clamp monthCount down whenever it no longer fits within the calendar
    // year for the newly chosen start month.
    "models.startMonth"(startMonth) {
      const max = 13 - startMonth;
      if (this.models.monthCount > max) this.models.monthCount = max;
    },
  },
  methods: {
    toggleAll() {
      // No-op on an empty list, so the link can't flip its own label to
      // "Deselect all" while there is nothing selected to deselect.
      if (!this.districts.length) return;

      this.models.districtIds = this.allSelected ? [] : this.districts.map((d) => d._id);
    },
    apply() {
      this.$emit("change", {
        // Every Apply generates all three scopes (INFS-1454). The work-scope
        // checkboxes are gone: all three were ticked by default and the page
        // shows one tab per scope anyway, so the control only ever removed
        // tabs the user then had to come back for. Read from scopes.js rather
        // than spelled out here, so the tab strip and this emit cannot
        // disagree about which scopes exist or in what order.
        scopes: [...KJP_SCOPE_CODES],
        year: this.models.year,
        startMonth: this.models.startMonth,
        monthCount: this.models.monthCount,
        asAtMonth: this.models.asAtMonth,
        districtIds: [...this.models.districtIds],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.kjp-district-list {
  max-height: 220px;
  overflow-y: auto;
}

.kjp-select-all {
  font-size: 0.75rem;
  color: #6ca9ee;
  text-decoration: underline;

  &:hover {
    color: #9ac6f4;
  }

  &.disabled {
    color: #6c757d;
    pointer-events: none;
  }
}
</style>
